import type { PrimitiveDef } from '../manifest';
import type { BenchNode, RunRecord } from '../runtime';

function schemaProperties(primitive: PrimitiveDef): Record<string, { type?: string; enum?: unknown[] }> {
  const schema = primitive.parameter_schema;
  if (!schema || typeof schema !== 'object') return {};
  return ((schema as { properties?: Record<string, { type?: string; enum?: unknown[] }> }).properties ?? {});
}

function coerceValue(type: string | undefined, value: string): unknown {
  if (type === 'number') return Number(value);
  if (type === 'integer') return Number.parseInt(value, 10);
  if (type === 'boolean') return value === 'true';
  return value;
}

export function ParameterInspector({
  primitive,
  node,
  currentRun,
  onChange,
  onApprove,
  onReject,
}: {
  primitive?: PrimitiveDef;
  node?: BenchNode;
  currentRun: RunRecord | null;
  onChange: (parameters: Record<string, unknown>) => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  if (!primitive || !node) {
    return (
      <section className="sp-widget" style={{ minHeight: 0 }}>
        <div className="sp-widget-header">
          <span>Inspector</span>
          <span className="mono">No selection</span>
        </div>
        <div className="sp-widget-body" style={{ color: 'var(--sp-text-muted)' }}>
          Select a graph node to edit its local V1 parameters.
        </div>
      </section>
    );
  }

  const properties = schemaProperties(primitive);
  const pausedHere = currentRun?.status === 'paused' && currentRun.pausedAtNodeId === node.id;

  return (
    <section className="sp-widget" style={{ minHeight: 0 }}>
      <div className="sp-widget-header">
        <span>Inspector</span>
        <span className="mono">{primitive.capability_class}</span>
      </div>
      <div className="sp-widget-body" style={{ display: 'grid', gap: 12 }}>
        <div>
          <strong style={{ color: 'var(--sp-text)' }}>{primitive.label}</strong>
          <code style={{ color: 'var(--sp-text-dim)', display: 'block', fontSize: 11, marginTop: 4 }}>
            {primitive.id}
          </code>
          <p style={{ color: 'var(--sp-text-muted)', fontSize: 12, margin: '8px 0 0' }}>
            {primitive.description}
          </p>
        </div>

        {Object.keys(properties).length > 0 ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {Object.entries(properties).map(([key, property]) => {
              const value = node.parameters[key] ?? '';
              return (
                <label key={key} style={{ color: 'var(--sp-text-muted)', display: 'grid', gap: 4, fontSize: 12 }}>
                  <span className="mono">{key}</span>
                  {Array.isArray(property.enum) ? (
                    <select
                      className="sp-input"
                      onChange={(event) => onChange({ ...node.parameters, [key]: event.target.value })}
                      value={String(value)}
                    >
                      {property.enum.map((option) => (
                        <option key={String(option)} value={String(option)}>
                          {String(option)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="sp-input"
                      onChange={(event) =>
                        onChange({ ...node.parameters, [key]: coerceValue(property.type, event.target.value) })
                      }
                      type={property.type === 'number' || property.type === 'integer' ? 'number' : 'text'}
                      value={String(value)}
                    />
                  )}
                </label>
              );
            })}
          </div>
        ) : (
          <div className="sp-empty" style={{ textAlign: 'left' }}>
            This primitive has no configurable parameters in the local manifest.
          </div>
        )}

        {pausedHere ? (
          <div
            style={{
              border: '1px solid var(--sp-warn)',
              borderRadius: 'var(--sp-radius-lg)',
              display: 'grid',
              gap: 8,
              padding: 12,
            }}
          >
            <strong style={{ color: 'var(--sp-warn)' }}>Approval gate paused</strong>
            <p style={{ color: 'var(--sp-text-muted)', fontSize: 12, margin: 0 }}>
              The run is waiting for a human decision. Approve resumes the local substrate wave and writes a
              decision into the run record.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sp-btn sp-btn-primary" onClick={onApprove} type="button">
                Approve and resume
              </button>
              <button className="sp-btn" onClick={onReject} type="button">
                Reject
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
